import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  CSSProperties,
} from 'react';
import memoize from 'memoize-one';
import {useResizeDetector} from 'react-resize-detector';
import {
  areEqual,
  FixedSizeGrid as Grid,
  GridChildComponentProps,
  GridOnItemsRenderedProps,
} from 'react-window';

const MEASUREMENT_TIMEOUT = 800;
const MIN_COLUMN_COUNT = 1;
const MIN_ROW_COUNT = 1;
const DEFAULT_DIMENSIONS = {width: 100, height: 100};
const OVERSCAN_ROW_COUNT = 2;

interface ItemType {
  id: string;
  type?: string;
  file?: File | string;
  url?: string;
  [key: string]: any;
}

interface ItemDimensions {
  width: number;
  height: number;
}

interface MeasurementStatus {
  [key: string]: boolean;
}

export interface StudioVirtualizedGridProps {
  data: ItemType[];
  columnWidth?: number;
  rowHeight?: number;
  children?: React.ComponentType<GridChildComponentProps>;
  onItemsRendered?: (args: GridOnItemsRenderedProps) => void;
  loadMore?: () => void;
  totalData?: number;
}

interface GridRef extends Grid {
  resetAfterIndices: (params: {
    columnIndex?: number;
    rowIndex?: number;
    shouldForceUpdate?: boolean;
  }) => void;
}

const dimensionCache = new Map<string, ItemDimensions>();

const createItemData = memoize(
  (
    items: ItemType[],
    columnCount: number,
    children: React.ComponentType<GridChildComponentProps>,
    dimensionCache: Map<string, ItemDimensions>,
    onItemMeasured: (id: string, width: number, height: number, type?: string) => void,
    initialDimensions: ItemDimensions,
    isMeasuring: boolean
  ) => ({
    items,
    children,
    columnCount,
    dimensionCache,
    onItemMeasured,
    initialDimensions,
    isMeasuring,
  })
);

const shouldUpdateItemDimensions = (
  cached: ItemDimensions | undefined,
  newDimensions: ItemDimensions
): boolean => {
  if (!cached) {
    return true;
  }
  return cached.width !== newDimensions.width || cached.height !== newDimensions.height;
};

const measureItem = (
  element: HTMLDivElement | null,
  itemData: ItemType,
  dimensionCache: Map<string, ItemDimensions>,
  onItemMeasured: (id: string, width: number, height: number, type?: string) => void
): void => {
  if (element) {
    const {width, height} = element.getBoundingClientRect();

    if (width > 0 && height > 0) {
      const dimensions = {width, height};
      const shouldUpdate = shouldUpdateItemDimensions(
        dimensionCache.get(itemData.id),
        dimensions
      );

      if (shouldUpdate) {
        dimensionCache.set(itemData.id, dimensions);
        onItemMeasured(itemData.id, width, height, itemData.type);
      }
    }
  }
};

const getItemsArrayFromSet = (set: Set<string>): string[] => Array.from(set);

const Row = React.memo(
  ({columnIndex, rowIndex, style, data: gridData}: GridChildComponentProps) => {
    const {
      items,
      columnCount,
      children,
      dimensionCache,
      onItemMeasured,
      initialDimensions,
      isMeasuring,
    } = gridData;

    const itemRef = useRef<HTMLDivElement>(null);
    const measurementComplete = useRef(false);
    const listIndex = columnIndex + columnCount * rowIndex;
    const itemData = items[listIndex];

    useEffect(() => {
      const shouldMeasure = itemRef.current && itemData && !measurementComplete.current;

      if (shouldMeasure) {
        measureItem(itemRef.current, itemData, dimensionCache, onItemMeasured);
        measurementComplete.current = true;
      }
    }, [itemData, dimensionCache, onItemMeasured]);

    if (!itemData) {
      return <div style={style as CSSProperties} />;
    }

    if (isMeasuring) {
      const measuringStyle: CSSProperties = {
        position: 'absolute',
        left: '-9999px',
        width: initialDimensions.width,
        height: initialDimensions.height,
      };

      return (
        <div
          ref={itemRef}
          style={measuringStyle}
          className={`grid-item ${itemData.type || ''}`}
        >
          {children({
            style: {},
            rowIndex,
            columnIndex,
            data: itemData,
          })}
        </div>
      );
    }

    const dimensions = dimensionCache.get(itemData.id) || initialDimensions;
    const itemStyle: CSSProperties = {
      ...(style as CSSProperties),
      width: dimensions.width,
      height: dimensions.height,
      position: 'absolute',
      top: style.top,
      left: style.left,
    };

    return (
      <div ref={itemRef} style={itemStyle} className={`grid-item ${itemData.type || ''}`}>
        {children({
          style: {},
          rowIndex,
          columnIndex,
          data: itemData,
        })}
      </div>
    );
  },
  areEqual
);

const calculateColumnCount = (width: number, columnWidth: number): number => {
  let count = MIN_COLUMN_COUNT;

  if (width > 0 && columnWidth > 0) {
    count = Math.max(MIN_COLUMN_COUNT, Math.floor(width / columnWidth));
  }

  return count;
};

const calculateRowCount = (dataLength: number, columnCount: number): number => {
  let count = 0;

  if (dataLength > 0) {
    count = Math.max(MIN_ROW_COUNT, Math.ceil(dataLength / columnCount));
  }

  return count;
};

const StudioVirtualizedGrid = ({
  data,
  totalData = 0,
  children = ({style, data}) => <div style={style}>{JSON.stringify(data)}</div>,
  rowHeight = DEFAULT_DIMENSIONS.height,
  columnWidth = DEFAULT_DIMENSIONS.width,
  loadMore = () => {},
  onItemsRendered = () => {},
  ...rest
}: StudioVirtualizedGridProps) => {
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isMeasuring, setIsMeasuring] = useState(true);
  const [measurementStatus, setMeasurementStatus] = useState<MeasurementStatus>({});
  const gridRef = useRef<GridRef>(null);
  const previousItems = useRef<Set<string>>(new Set(data.map(item => item.id)));
  const measurementTimeout = useRef<NodeJS.Timeout>();

  const {width = 0, height = 0, ref} = useResizeDetector({
    handleWidth: true,
    handleHeight: true,
    refreshMode: 'debounce',
    refreshRate: 100,
    skipOnMount: false,
  });

  const columnCount = useMemo(() => calculateColumnCount(width, columnWidth), [
    width,
    columnWidth,
  ]);

  const rowCount = useMemo(() => calculateRowCount(data.length, columnCount), [
    data.length,
    columnCount,
  ]);

  const handleNewData = useCallback(() => {
    const currentItems = new Set(data.map(item => item.id));
    const hasNewItems = getItemsArrayFromSet(currentItems).some(
      id => !previousItems.current.has(id)
    );
    const hasRemovedItems = getItemsArrayFromSet(previousItems.current).some(
      id => !currentItems.has(id)
    );

    if (hasNewItems || hasRemovedItems) {
      setIsMeasuring(true);
      setMeasurementStatus({});
      dimensionCache.clear();
      setForceUpdate(prev => prev + 1);
      previousItems.current = currentItems;
    }
  }, [data]);

  useEffect(() => {
    handleNewData();

    if (measurementTimeout.current) {
      clearTimeout(measurementTimeout.current);
    }

    measurementTimeout.current = setTimeout(() => {
      setIsMeasuring(false);
    }, MEASUREMENT_TIMEOUT);

    return () => {
      if (measurementTimeout.current) {
        clearTimeout(measurementTimeout.current);
      }
    };
  }, [handleNewData]);

  const handleItemMeasured = useCallback(
    (itemId: string, itemWidth: number, itemHeight: number, itemType?: string) => {
      if (itemWidth <= 0 || itemHeight <= 0) return;

      setMeasurementStatus(prev => ({
        ...prev,
        [itemId]: true,
      }));

      const newDimensions = {width: itemWidth, height: itemHeight};
      const shouldUpdate = shouldUpdateItemDimensions(
        dimensionCache.get(itemId),
        newDimensions
      );

      if (shouldUpdate) {
        dimensionCache.set(itemId, newDimensions);
        // Solo filtramos por tipo si existe
        const itemsOfSameType = itemType
          ? data.filter(item => item.type === itemType)
          : data;
        const allMeasured = itemsOfSameType.every(item => measurementStatus[item.id]);

        if (allMeasured) {
          setIsMeasuring(false);
          setForceUpdate(prev => prev + 1);
          gridRef.current?.resetAfterIndices({
            columnIndex: 0,
            rowIndex: 0,
            shouldForceUpdate: true,
          });
        }
      }
    },
    [data, measurementStatus]
  );

  const initialDimensions = useMemo(() => ({width: columnWidth, height: rowHeight}), [
    columnWidth,
    rowHeight,
  ]);

  const itemData = useMemo(
    () =>
      createItemData(
        data,
        columnCount,
        children,
        dimensionCache,
        handleItemMeasured,
        initialDimensions,
        isMeasuring
      ),
    [data, columnCount, children, handleItemMeasured, initialDimensions, isMeasuring]
  );

  const handleItemsRendered = useCallback(
    (args: GridOnItemsRenderedProps) => {
      if (isMeasuring) {
        return;
      }

      const {visibleRowStopIndex, overscanRowStopIndex} = args;
      const shouldLoadMore =
        visibleRowStopIndex === overscanRowStopIndex && data.length < totalData;

      if (shouldLoadMore) {
        loadMore();
      }

      onItemsRendered(args);
    },
    [data.length, totalData, loadMore, onItemsRendered, isMeasuring]
  );

  const containerStyle: CSSProperties = {
    minHeight: rowHeight * Math.min(data.length, 3),
  };

  if (!width || !height) {
    return (
      <div
        ref={ref}
        className="studio-virtualized-grid-container"
        style={containerStyle}
      />
    );
  }

  if (isMeasuring) {
    return (
      <div
        ref={ref}
        className="studio-virtualized-grid-container measuring"
        style={containerStyle}
      >
        <Grid
          {...rest}
          width={0}
          height={0}
          rowHeight={rowHeight}
          columnWidth={columnWidth}
          columnCount={1}
          rowCount={data.length}
          itemData={itemData}
          className="virtualized-grid"
        >
          {Row}
        </Grid>
      </div>
    );
  }

  return (
    <div ref={ref} className="studio-virtualized-grid-container" style={containerStyle}>
      <Grid
        {...rest}
        ref={gridRef}
        width={width}
        height={height}
        itemData={itemData}
        rowHeight={rowHeight}
        columnWidth={columnWidth}
        columnCount={columnCount}
        className="virtualized-grid"
        overscanRowCount={OVERSCAN_ROW_COUNT}
        key={`grid-${forceUpdate}-${width}-${height}`}
        rowCount={rowCount + (data.length < totalData ? 1 : 0)}
        onItemsRendered={handleItemsRendered}
      >
        {Row}
      </Grid>
    </div>
  );
};

export default StudioVirtualizedGrid;
